import { useEffect, useRef, useState } from "react";
import { Reveal } from "@/components/Reveal";
import { MERCH, MERCH_STORE_URL, API } from "@/assets";
import { ShoppingBag, ArrowUpRight, Truck, ShieldCheck, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (url) => (url && url.startsWith("http") ? url : `${BACKEND}${url}`);

const FALLBACK_PRODUCTS = [
  { name: "Cold-Blooded Culture Attire™ Hoodie", tag: "Custom Apparel", price: "From $45", image: MERCH.hoodie, store_url: MERCH_STORE_URL },
  { name: "Slither N Sparkle™ Tee", tag: "Fandom Essentials", price: "From $28", image: MERCH.tshirt, store_url: MERCH_STORE_URL },
  { name: "Slitherin' Soles™ High-Tops", tag: "Footwear", price: "From $95", image: MERCH.sneakers, store_url: MERCH_STORE_URL },
  { name: "Cold-Blooded Culture Attire™ Snapback", tag: "Accessories", price: "From $32", image: MERCH.cap, store_url: MERCH_STORE_URL },
];

const MerchCardImage = ({ p, i }) => {
  const photos = [p.image, ...(p.gallery || [])].filter(Boolean);
  const [idx, setIdx] = useState(0);
  const touchX = useRef(null);
  const many = photos.length > 1;
  const goTo = (e, n) => {
    e.preventDefault();
    e.stopPropagation();
    setIdx((n + photos.length) % photos.length);
  };

  return (
    <a
      href={p.store_url || MERCH_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open ${p.name} in the store`}
      className="block relative aspect-square overflow-hidden cursor-pointer"
      data-testid={`merch-photo-link-${i}`}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current === null || !many) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 40) setIdx((idx + (dx < 0 ? 1 : -1) + photos.length) % photos.length);
        touchX.current = null;
      }}
    >
      <img
        src={mediaUrl(photos[idx])}
        alt={`${p.name}${many ? ` — photo ${idx + 1} of ${photos.length}` : ""}`}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        data-testid={`merch-photo-${i}`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-transparent to-transparent" />
      <span className="absolute top-3 left-3 font-mono text-[9px] uppercase tracking-[0.15em] bg-obsidian/80 backdrop-blur-sm border border-crystal/40 text-ice/80 rounded-full px-2.5 py-1">
        {p.tag}
      </span>
      <span className="absolute top-3 right-3 w-8 h-8 rounded-full bg-venom text-obsidian flex items-center justify-center opacity-0 group-hover:opacity-100 scale-75 group-hover:scale-100 transition-all duration-300">
        <ArrowUpRight size={15} />
      </span>
      {many && (
        <>
          <button
            onClick={(e) => goTo(e, idx - 1)}
            aria-label="Previous photo"
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-obsidian/70 backdrop-blur-sm border border-white/15 text-ice/80 flex items-center justify-center opacity-0 group-hover:opacity-100 hover:border-venom hover:text-venom transition-all"
            data-testid={`merch-photo-prev-${i}`}
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={(e) => goTo(e, idx + 1)}
            aria-label="Next photo"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-obsidian/70 backdrop-blur-sm border border-white/15 text-ice/80 flex items-center justify-center opacity-0 group-hover:opacity-100 hover:border-venom hover:text-venom transition-all"
            data-testid={`merch-photo-next-${i}`}
          >
            <ChevronRight size={16} />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid={`merch-photo-dots-${i}`}>
            {photos.map((_, j) => (
              <span key={j} className={`h-1.5 rounded-full transition-all duration-300 ${j === idx ? "w-5 bg-venom" : "w-1.5 bg-white/40"}`} />
            ))}
          </div>
        </>
      )}
    </a>
  );
};

const BRAND_ART = {
  banner: "/api/files/dge-records/brand/banner-25c37ade-cfaf-46a5-9e85-740dc7370a2a.jpg",
  art1: "/api/files/dge-records/brand/art1-55fab82a-ce6c-42c0-a7b5-909a4a414cc0.jpg",
  art2: "/api/files/dge-records/brand/art2-3a5721f1-c307-4640-851a-23591c650bd6.jpg",
};

const PERKS = [
  { icon: Sparkles, text: "Limited drops, members-first" },
  { icon: Truck, text: "Ships worldwide" },
  { icon: ShieldCheck, text: "Official DGE gear only" },
];

export const MerchSection = () => {
  const [products, setProducts] = useState(FALLBACK_PRODUCTS);

  useEffect(() => {
    fetch(`${API}/merch`)
      .then((r) => r.json())
      .then((d) => {
        if (d.items && d.items.length > 0) setProducts(d.items);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="merch" className="relative py-20 lg:py-28 bg-charcoal/40" data-testid="merch-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-venom font-medium mb-4">
              Fan Merchandise &amp; Apparel
            </p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight">
              Wear the label.
              <br />
              <span className="text-stroke-ice">Rep the movement.</span>
            </h2>
          </div>
          <a
            href={MERCH_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 border border-venom/50 text-venom font-semibold text-sm px-6 py-3 rounded-full hover:bg-venom hover:text-obsidian active:scale-95 transition-all duration-300"
            data-testid="merch-visit-store-cta"
          >
            Visit Boa&apos;s 33rd Dimension™
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </Reveal>

        <Reveal delay={0.08}>
          <a
            href={MERCH_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative block mt-10 rounded-2xl overflow-hidden border border-crystal/30 hover:border-venom/60 transition-colors duration-500"
            data-testid="merch-brand-banner"
          >
            <img
              src={mediaUrl(BRAND_ART.banner)}
              alt="Boa's 33rd Dimension™ crystal showroom with hand-designed apparel"
              loading="lazy"
              className="w-full h-56 sm:h-72 lg:h-80 object-cover object-center group-hover:scale-[1.03] transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-obsidian/90 via-obsidian/40 to-transparent flex items-center">
              <div className="px-6 sm:px-10 max-w-md">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-venom">Official Streetwear Division</p>
                <h3 className="mt-2 font-display text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-tight">
                  Boa&rsquo;s 33rd Dimension&trade;
                </h3>
                <p className="mt-2 text-xs sm:text-sm text-ice/70 leading-relaxed">
                  Cold-Blooded Culture, the official signature line — every piece of our streetwear
                  is hand-designed original artwork by <span className="text-venom font-semibold">Heather Da Boa</span>.
                </p>
                <span className="mt-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em] text-venom">
                  Enter the dimension <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </div>
          </a>
        </Reveal>

        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {products.map((p, i) => (
            <Reveal key={p.m_id || p.name} delay={Math.min(i, 4) * 0.08}>
              <div
                className="group bg-charcoal border border-crystal/20 rounded-2xl overflow-hidden hover:border-venom/60 hover:-translate-y-1.5 hover:glow-venom transition-all duration-500"
                data-testid={`merch-card-${i}`}
              >
                <MerchCardImage p={p} i={i} />
                <div className="p-4 sm:p-5">
                  <h3 className="font-display text-white text-sm sm:text-base font-bold">{p.name}</h3>
                  <p className="mt-1 text-xs text-ice/50">{p.price}</p>
                  <a
                    href={p.store_url || MERCH_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 bg-venom text-obsidian font-bold text-xs sm:text-sm px-4 py-2.5 rounded-full hover:glow-venom hover:scale-[1.03] active:scale-95 transition-all duration-300"
                    data-testid={`merch-shop-btn-${i}`}
                  >
                    <ShoppingBag size={14} />
                    Shop This Item
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-14" data-testid="merch-art-strip">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-crystal text-center mb-6">
              The art behind the label — hand-designed by Heather Da Boa
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
              {[BRAND_ART.art1, BRAND_ART.art2].map((src, i) => (
                <div key={src} className="rounded-2xl overflow-hidden border border-crystal/25 hover:border-venom/50 transition-colors duration-500">
                  <img
                    src={mediaUrl(src)}
                    alt={`Original Boa's 33rd Dimension™ artwork ${i + 1} by Heather Da Boa`}
                    loading="lazy"
                    className="w-full aspect-square object-cover hover:scale-[1.04] transition-transform duration-700"
                    data-testid={`merch-art-${i}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-3">
            {PERKS.map((perk) => {
              const Icon = perk.icon;
              return (
                <div key={perk.text} className="flex items-center gap-2 text-ice/55">
                  <Icon size={15} className="text-venom" />
                  <span className="text-xs sm:text-sm">{perk.text}</span>
                </div>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
